import { apiClient } from './authService';
import { round3 } from '../utils/money';

const salesService = {
  // Get all sales
  getAllSales: async () => {
    try {
      const response = await apiClient.get('sales/');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching sales' };
    }
  },

  // Historique d'achat du client pour le mode « Retour » du panier :
  // { productId: { bought, returned, last_date, last_price, last_remise_pct } }.
  getReturnable: async (customerId, productIds) => {
    const ids = Array.isArray(productIds) ? productIds.join(',') : String(productIds || '');
    const response = await apiClient.get(`sales/customer/${customerId}/returnable/?products=${ids}`);
    return response.data || {};
  },

  // Server-side paginated list — used by SalesList and SellerHistorySales.
  getSalesPage: async ({ page = 1, pageSize = 50, search = '', status = '', clientName = '', phone = '', productName = '', startDate = '', endDate = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (clientName) params.set('client_name', clientName);
      if (phone) params.set('client_phone', phone);
      if (productName) params.set('product_name', productName);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const response = await apiClient.get(`sales/?${params.toString()}`);
      const data = response.data;
      if (Array.isArray(data)) return { results: data, count: data.length };
      return { results: data?.results || [], count: data?.count || 0 };
    } catch (error) {
      console.error('Error fetching sales page:', error);
      throw error.response?.data || { message: 'Error fetching sales' };
    }
  },
  
  // Read-only KPI aggregates (CA / encaissé / crédit / count) for the SAME filters
  // as getSalesPage — powers the Historique des Ventes summary bar (whole filtered set).
  getSalesSummary: async ({ search = '', status = '', clientName = '', phone = '', productName = '', startDate = '', endDate = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (clientName) params.set('client_name', clientName);
      if (phone) params.set('client_phone', phone);
      if (productName) params.set('product_name', productName);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const response = await apiClient.get(`sales/summary/?${params.toString()}`);
      return response.data || { ca: 0, encaisse: 0, credit_paye: 0, credit: 0, count: 0, services_count: 0, services_total: 0 };
    } catch (error) {
      console.error('Error fetching sales summary:', error);
      return { ca: 0, encaisse: 0, credit_paye: 0, credit: 0, count: 0, services_count: 0, services_total: 0 };
    }
  },

  // Credit repayments (journal) received in the period — shown as their own rows
  // in the Historique des Ventes « Produits vendus » list.
  getCreditPayments: async ({ clientName = '', phone = '', startDate = '', endDate = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (clientName) params.set('client_name', clientName);
      if (phone) params.set('client_phone', phone);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const response = await apiClient.get(`sales/credit-payments/?${params.toString()}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching credit payments:', error);
      return [];
    }
  },

  // Get sales with filters
  getFilteredSales: async (filters) => {
    try {
      // Convert filters to query parameters
      const params = {};
      if (filters.clientName) params.client_name = filters.clientName;
      if (filters.phone) params.client_phone = filters.phone;
      if (filters.productName) params.product_name = filters.productName;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      
      const response = await apiClient.get('sales/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered sales:', error);
      throw error.response?.data || { message: 'Error fetching filtered sales' };
    }
  },
  
  // Get unpaid/partially paid sales
  getUnpaidSales: async () => {
    try {
      const response = await apiClient.get('sales/unpaid/');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching unpaid sales' };
    }
  },
  
  // Update payment for a sale
  updatePayment: async (saleId, paymentData) => {
    try {
      const response = await apiClient.patch(`sales/${saleId}/payment/`, paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error updating payment' };
    }
  },

  // Get sale by ID
  getSaleById: async (id) => {
    try {
      const response = await apiClient.get(`sales/${id}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching sale details' };
    }
  },

  // Create new sale (supports multiple products, services, and discounts)
  createSale: async (saleData) => {
    try {
      // Handle both old single product format and new multiple products format
      let transformedData;
      
      if (saleData.products && Array.isArray(saleData.products)) {
        // New multiple products format — every montant is quantized au millime
        // (round3) BEFORE the POST, so no float dust ever reaches the backend.
        const productsSubTotal = saleData.products.reduce((total, product) => {
          return total + round3(product.quantity * product.price);
        }, 0);

        // Calculate services subtotal (already includes service discounts)
        const servicesSubTotal = (saleData.services || []).reduce((total, service) => {
          const finalPrice = service.remise > 0
            ? round3(service.price * (1 - service.remise / 100))
            : service.price;
          return total + round3(service.quantity * finalPrice);
        }, 0);

        const subTotal = round3(productsSubTotal + servicesSubTotal);
        const discountAmount = round3(saleData.discount || 0);
        const grandTotal = round3(subTotal - discountAmount);
        const amountPaid = round3(parseFloat(saleData.amount_paid) || 0);
        const credit = round3(grandTotal - amountPaid);

        transformedData = {
          customer: saleData.client, // customer ID
          sub_total: subTotal,
          grand_total: grandTotal,
          tax_amount: 0,
          tax_percentage: 0,
          discount_amount: discountAmount,
          discount_percentage: saleData.is_discount_percent ? saleData.discount : 0,
          amount_payed: amountPaid,
          amount_change: amountPaid > grandTotal ? round3(amountPaid - grandTotal) : 0,
          credit: credit > 0 ? credit : 0,
          // Points spent as tender — deducted server-side from the cashback
          // balance in the same transaction that credits earned points.
          points_used: parseFloat(saleData.points_pay) || 0,
          // Réservations vendues par cette vente (« Vendre au client », ou
          // produit bloqué acheté par SON client) : le backend les clôture
          // (Vendue) dans la même transaction que la vente.
          reservation_ids: saleData.reservation_ids
            || (saleData.reservation_id ? [saleData.reservation_id] : []),
          // Rendez-vous de soin ouvert par « Terminer » : le backend le passe
          // Terminé + Payé dans la même transaction que la vente.
          session_ids: saleData.session_ids || [],
          // Retour client (bouton « Retour » du panier) : le backend enregistre
          // une vente NÉGATIVE (stock +, remboursement, points repris) ;
          // refund_mode 'credit' déduit d'abord du crédit du client.
          ...(saleData.is_return ? { is_return: true, refund_mode: saleData.refund_mode || 'cash' } : {}),
          // Personne reconnue à la caisse (code) — identityGate.actorFields.
          // Dans le CORPS : un rejeu hors ligne ne garde que lui. Absent = la
          // vente garde seulement le compte connecté.
          ...(saleData.actor ? { actor: saleData.actor, actor_token: saleData.actor_token || '', actor_method: saleData.actor_method || 'pin' } : {}),
          details: saleData.products.map(product => ({
            product: product.product, // product ID
            quantity: product.quantity,
            price: product.price
          })),
          service_details: (saleData.services || []).map(service => ({
            service: service.service, // service ID
            quantity: service.quantity,
            price: service.price,
            remise: service.remise || 0
          }))
        };
      } else {
        // Old single product format (backward compatibility)
        const totalPrice = round3(saleData.quantity * saleData.price);
        const amountPaid = round3(parseFloat(saleData.amount_paid) || 0);
        const credit = round3(totalPrice - amountPaid);
        
        transformedData = {
          customer: saleData.client, // customer ID
          sub_total: totalPrice,
          grand_total: totalPrice,
          tax_amount: 0,
          tax_percentage: 0,
          amount_payed: amountPaid,
          amount_change: 0,
          credit: credit > 0 ? credit : 0,
          details: [
            {
              product: saleData.product, // product ID
              quantity: saleData.quantity,
              price: saleData.price
            }
          ]
        };
      }
      
      console.log('Sending sale data to backend:', transformedData);
      const response = await apiClient.post('sales/', transformedData);
      return response.data;
    } catch (error) {
      console.error('Error creating sale:', error);
      throw error.response?.data || { message: 'Error creating sale' };
    }
  },

  // Generate receipt
  getReceipt: async (id) => {
    try {
      const response = await apiClient.get(`sales/${id}/receipt/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error generating receipt' };
    }
  }
};

export default salesService;
